import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Box from "@redbubble/design-system/react/Box";
import Text from "@redbubble/design-system/react/Text";
import TextLink from "@redbubble/design-system/react/TextLink";
import Avatar from "@redbubble/design-system/react/Avatar";
import * as constants from "@redbubble/design-system/react/constants";
import { localizedPath } from "../../../../../../lib/routing";
import { formatDateDistance } from "../../../../../../lib/dateHelpers";
import ReviewOptions from "./ReviewOptions";
import messages from "../messages";
import styles from "../CommentsSection.scss";
import ErrorMessage from "../ErrorMessage";
import useDeleteComment from "../../../../../../containers/apollo/useDeleteComment";

const Comment = ({
  locale,
  artistId,
  comment,
  createdAt,
  user: {
    id: authorId,
    avatar: authorAvatar,
    displayName: authorDisplayName,
    userName: authorUsername,
  },
  workId,
  commentId,
  canDelete,
  canReport,
}) => {
  const { deleteComment, deleteCommentLoading, deleteCommentError } =
    useDeleteComment(workId, commentId);

  const isAuthorArtist = useMemo(
    () => artistId === authorId,
    [artistId, authorId]
  );

  const showReviewOptions = useMemo(
    () => canDelete || canReport,
    [canDelete, canReport]
  );

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        paddingRight="xs"
        paddingLeft="xs"
        paddingBottom="m"
      >
        <Box display="flex" flexDirection="row" alignItems="center">
          <TextLink
            href={localizedPath(locale, `/people/${authorUsername}/shop`)}
            className={styles.navLink}
          >
            {authorAvatar && (
              <Avatar src={authorAvatar} size={constants.TINY} loadOnVisible />
            )}
          </TextLink>
          <Box marginLeft={authorAvatar ? 0.5 : 0}>
            <TextLink
              href={localizedPath(locale, `/people/${authorUsername}/shop`)}
              type="display5"
              className={styles.navLink}
            >
              {authorDisplayName}
            </TextLink>
          </Box>
          {isAuthorArtist && (
            <Box marginLeft="xs">
              <Text type="caption" muted>
                <FormattedMessage {...messages.artistCaption} />
              </Text>
            </Box>
          )}
        </Box>
        <Box>
          <Text type="caption" muted>
            {formatDateDistance(createdAt, locale)}
          </Text>
        </Box>
      </Box>
      <Box className={styles.messageBoxWrapper}>
        <Box className={styles.messageBoxTriangleWrapper}>
          <Box className={styles.messageBoxGreyTriangle} />
          <Box className={styles.messageBoxWhiteOverlay} />
        </Box>
        <Box
          className={styles.messageBoxLayout}
          display="flex"
          alignItems="center"
        >
          <Box className={styles.messageBox} marginRight="xs">
            <Text type="body">{comment}</Text>
          </Box>
          {showReviewOptions && (
            <Box>
              <ReviewOptions
                commentId={commentId}
                deleteComment={deleteComment}
                deleteCommentLoading={deleteCommentLoading}
                canDelete={canDelete}
                canReport={canReport}
              />
            </Box>
          )}
        </Box>
      </Box>
      {deleteCommentError && (
        <Box marginLeft="xs">
          <ErrorMessage message={messages.deletingCommentErrorMessage} />
        </Box>
      )}
    </Box>
  );
};

Comment.propTypes = {
  locale: PropTypes.string.isRequired,
  artistId: PropTypes.string.isRequired,
  comment: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  user: PropTypes.shape({
    avatar: PropTypes.string,
    displayName: PropTypes.string,
    firstName: PropTypes.string,
    id: PropTypes.string,
    lastName: PropTypes.string,
    userName: PropTypes.string,
  }).isRequired,
  workId: PropTypes.string.isRequired,
  commentId: PropTypes.string.isRequired,
  canDelete: PropTypes.bool.isRequired,
  canReport: PropTypes.bool.isRequired,
};

export default React.memo(Comment);
